/**
 * A referral code that arrived in a link, remembered until it can be used.
 *
 * The realistic path is a text message: somebody taps a friend's link, lands on
 * the app, signs up, is walked through setting a pantry up, and only reaches
 * the screen with the code box several minutes and one redirect later. The code
 * is in the URL for the first instant of that and gone by the second, so it is
 * put somewhere that survives -- otherwise the person who was actually referred
 * is the one person who never gets to say so.
 *
 * Read once and stripped from the address bar, so a refresh, a bookmark, or a
 * link shared onwards does not carry somebody else's code along with it.
 *
 * localStorage rather than a cookie: this is a convenience for one browser, it
 * never needs to reach the server on its own, and a cookie would have to be
 * declared. Every access is wrapped, because a private window, a browser set to
 * block site data, or an embedded webview will throw rather than return null --
 * and losing a referral code is not worth a blank screen.
 */

const KEY = 'pantry.referral';

/** The code carried by this page load, if any, moved somewhere it will last. */
export function captureReferral() {
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('refer');
    if (!code) return;
    window.localStorage.setItem(KEY, code.slice(0, 32));
    url.searchParams.delete('refer');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } catch { /* no storage, or no history: the code box still takes it by hand */ }
}

/** Whatever a link left here, for the code box to start out holding. */
export function readReferral() {
  try {
    return window.localStorage.getItem(KEY) ?? '';
  } catch {
    return '';
  }
}

/** Used, or refused for good. Either way it should stop reappearing. */
export function clearReferral() {
  try {
    window.localStorage.removeItem(KEY);
  } catch { /* nothing to forget */ }
}
